import { Exchange, ExchangeRequestStatus, SecondaryCircuits } from 'Quotes/types/store';
import { Supplier } from 'Quotes/types/supplier';
import {
  P2PCircuitData,
  SecondaryCircuitStatus,
} from 'shared/components/molecules/SecondaryCircuits/SecondaryCircuits';
import {
  getExchangeStatus,
  HandleSelectedCircuit,
  hasAEnd,
  hasBEnd,
  useExchanges,
} from 'shared/components/molecules/SecondaryCircuits/data/useSecondaryCircuits';

export function useP2PCircuitData(
  secondaryCircuits: SecondaryCircuits | undefined,
  handleSelectedCircuit: HandleSelectedCircuit,
  status: ExchangeRequestStatus,
  aEndAccessType: string | null,
  bEndAccessType: string | null,
  aEndPrimaryPopId: string | null,
  bEndPrimaryPopId: string | null | undefined
): P2PCircuitData {
  const isAEndOnNet = aEndAccessType === Supplier.NONE;
  const isBEndOnNet = bEndAccessType === Supplier.NONE;

  const aEndExchanges = isAEndOnNet ? undefined : secondaryCircuits?.aEndExchanges;
  const bEndExchanges = isBEndOnNet ? undefined : secondaryCircuits?.bEndExchanges;

  const aExchanges = useExchanges(
    aEndExchanges,
    secondaryCircuits?.selectedAEndId,
    (exchange: Exchange) =>
      handleSelectedCircuit(
        getCircuitIdFromPopIds(
          isAEndOnNet ? resolveAEndPopId() : exchange.popId,
          isBEndOnNet ? resolveBEndPopId() : bExchanges.selected?.popId
        )
      )
  );
  const bExchanges = useExchanges(
    bEndExchanges,
    secondaryCircuits?.selectedBEndId,
    (exchange: Exchange) =>
      handleSelectedCircuit(
        getCircuitIdFromPopIds(
          isAEndOnNet ? resolveAEndPopId() : aExchanges.selected?.popId,
          isBEndOnNet ? resolveBEndPopId() : exchange.popId
        )
      )
  );

  const resolveAEndPopId = () => {
    if (!isAEndOnNet) return aExchanges.selected?.popId;
    return aExchanges.selectedExchangeId ?? aEndPrimaryPopId ?? undefined;
  };

  const resolveBEndPopId = () => {
    if (!isBEndOnNet) return bExchanges.selected?.popId;
    return bExchanges.selectedExchangeId ?? bEndPrimaryPopId ?? undefined;
  };

  const getCircuitId = (aEnd: Exchange | undefined, bEnd: Exchange | undefined) =>
    getCircuitIdFromPopIds(
      isAEndOnNet ? resolveAEndPopId() : aEnd?.popId,
      isBEndOnNet ? resolveBEndPopId() : bEnd?.popId
    );

  const processors = [] as ((secondaryCircuits: SecondaryCircuits) => SecondaryCircuits)[];
  if (!isAEndOnNet) processors.push((circuits) => hasAEnd(circuits));
  if (!isBEndOnNet) processors.push((circuits) => hasBEnd(circuits));

  const getStatus = () => {
    if (isAEndOnNet && !isBEndOnNet) {
      return getExchangeStatus(status, bExchanges.exchanges);
    }
    if (!isAEndOnNet && isBEndOnNet) {
      return getExchangeStatus(status, aExchanges.exchanges);
    }
    return getExchangeStatus(status, bExchanges.exchanges, aExchanges.exchanges);
  };

  return {
    aEnd: aExchanges,
    bEnd: bExchanges,
    getSelectedCircuitId: () => getCircuitId(aExchanges.selected, bExchanges.selected),
    getCircuitId: getCircuitId,
    status: getStatus,
    canLoadPrices: () => !!resolveAEndPopId() && !!resolveBEndPopId(),
    isLoading: false,
    processors: processors,
    type: 'P2P',
  };
}

function getCircuitIdFromPopIds(aEndPopId: string | undefined, bEndPopId: string | undefined) {
  if (!aEndPopId || !bEndPopId) return undefined;
  return `datacentre-${aEndPopId}_datacentre-${bEndPopId}`;
}

// OpticalP2P doesn't support RO2
// However, since we can change product type from P2P to Optical P2P and back
// React throws an exception because the number of hooks changes between renders
// The correct way of fixing this is to use different components for different product types
// But in the meantime, we can fix it by just ensuring we call the same number of hooks for Optical P2P as P2P
export function useDummyOpticalP2PCircuitData(
  secondaryCircuits: SecondaryCircuits | undefined,
  handleSelectedCircuit: HandleSelectedCircuit
): P2PCircuitData {
  const aExchanges = useExchanges(
    secondaryCircuits?.aEndExchanges,
    secondaryCircuits?.selectedAEndId,
    (exchange: Exchange) => handleSelectedCircuit(getCircuitIdFromPopIds(exchange.popId, bExchanges.selected?.popId))
  );
  const bExchanges = useExchanges(
    secondaryCircuits?.bEndExchanges,
    secondaryCircuits?.selectedBEndId,
    (exchange: Exchange) => handleSelectedCircuit(getCircuitIdFromPopIds(aExchanges.selected?.popId, exchange.popId))
  );
  return {
    aEnd: aExchanges,
    bEnd: bExchanges,
    canLoadPrices: (): boolean => false,
    getCircuitId: (): string | undefined => undefined,
    getSelectedCircuitId: (): string | undefined => undefined,
    isLoading: false,
    processors: [],
    status: () => SecondaryCircuitStatus.SHOW_CHECK_NOW,
    type: 'P2P',
  };
}
